// Pure helpers for OperatorPricingProfile (no React, no API).
// Kept in its own file so unit tests can import it without dragging in axios.

export const emptyProfile = (operatorId, currency) => ({
  operator_id: operatorId,
  currency: currency || 'EUR',
  bill_deadhead_to_pickup: false,
  bill_return_to_base: false,
  vehicle_bands: [],
  zone_overrides: [],
  route_overrides: [],
  operator_only_zones: [],
  operator_only_routes: [],
});

// Strip transient UI Ids (tmp-*) so the backend can assign real ones.
const sanitizeOperatorOnly = (items) => (items || []).map((item) => {
  const next = { ...item };
  if (typeof next.Id === 'string' && next.Id.startsWith('tmp-')) {
    delete next.Id;
  }
  return next;
});

const numberOrUndefined = (v) => {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
};

const sanitizeBandValue = (band, currency) => {
  const sanitized = { up_to_km: Number(band.up_to_km || 0) };
  if (currency === 'EUR') {
    sanitized.flat_rate_eur = numberOrUndefined(band.flat_rate_eur);
    sanitized.per_km_eur = numberOrUndefined(band.per_km_eur);
  } else {
    sanitized.flat_rate_huf = numberOrUndefined(band.flat_rate_huf);
    sanitized.per_km_huf = numberOrUndefined(band.per_km_huf);
  }
  Object.keys(sanitized).forEach((k) => sanitized[k] === undefined && delete sanitized[k]);
  return sanitized;
};

const sanitizeHourly = (hourly, currency) => {
  if (!hourly) return undefined;
  const rateField = currency === 'EUR' ? 'hourly_rate_eur' : 'hourly_rate_huf';
  const waitingField = currency === 'EUR' ? 'per_hour_eur' : 'per_hour_huf';
  const rate = numberOrUndefined(hourly[rateField]);
  if (rate === undefined) return undefined;
  const waitingValue = numberOrUndefined(hourly.waiting_time?.[waitingField]);
  return {
    [rateField]: rate,
    minimum_hours: Number(hourly.minimum_hours || 1),
    ...(waitingValue !== undefined ? { waiting_time: { [waitingField]: waitingValue } } : {}),
  };
};

export const sanitizeProfileForApi = (profile) => ({
  operator_id: profile.operator_id,
  currency: profile.currency,
  bill_deadhead_to_pickup: profile.bill_deadhead_to_pickup === true,
  bill_return_to_base: profile.bill_return_to_base === true,
  vehicle_bands: (profile.vehicle_bands || []).map((entry) => ({
    vehicle_category_id: entry.vehicle_category_id,
    bands: (entry.bands || []).map((b) => sanitizeBandValue(b, profile.currency)),
    ...(entry.hourly ? { hourly: sanitizeHourly(entry.hourly, profile.currency) } : {}),
  })),
  zone_overrides: (profile.zone_overrides || []).map((zo) => {
    if (zo.mode === 'opt_out') {
      return { service_area_id: zo.service_area_id, mode: 'opt_out' };
    }
    return {
      service_area_id: zo.service_area_id,
      mode: 'override',
      ...(zo.price_multiplier !== undefined && zo.price_multiplier !== null
        ? { price_multiplier: Number(zo.price_multiplier) } : {}),
      ...(zo.flat_rate !== undefined && zo.flat_rate !== '' && zo.flat_rate !== null
        ? { flat_rate: Number(zo.flat_rate) } : {}),
      ...(zo.flat_rate_applies_to ? { flat_rate_applies_to: zo.flat_rate_applies_to } : {}),
    };
  }),
  route_overrides: (profile.route_overrides || []).map((ro) => {
    if (ro.mode === 'opt_out') {
      return { predefined_route_id: ro.predefined_route_id, mode: 'opt_out' };
    }
    return {
      predefined_route_id: ro.predefined_route_id,
      mode: 'override',
      category_prices: (ro.category_prices || []).map((cp) => ({
        vehicle_category_id: cp.vehicle_category_id,
        price: Number(cp.price),
      })),
    };
  }),
  operator_only_zones: sanitizeOperatorOnly(profile.operator_only_zones),
  operator_only_routes: sanitizeOperatorOnly(profile.operator_only_routes),
});
